import React, { useEffect, useCallback, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import MainLayout from "./MainLayout";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";

const AccountLayout = () => {
  const navigate = useNavigate();
  const { token, hydrated, isAuthenticated, logout, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const fetchProfile = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const data = await authService.me(token);
      setProfile(data);
      updateUser({
        id: data._id || data.id,
        fullName: data.fullName,
        email: data.email,
        username: data.username,
        role: data.role,
        avatar: data.avatar,
        authProvider: data.authProvider,
        mustChangePassword: Boolean(data.mustChangePassword),
        adminRole: data.adminRole,
        permissions: data.permissions,
      });
    } catch (e) {
      if (e?.status === 401) {
        logout();
        navigate("/login", { replace: true });
        return;
      }
      setLoadError(e.message || "Lỗi tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, [token, navigate, logout, updateUser]);

  useEffect(() => {
    if (!hydrated) return;
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }
    fetchProfile();
  }, [hydrated, isAuthenticated, navigate, fetchProfile]);

  if (!hydrated || loading) {
    return (
      <MainLayout hideFooter>
        <div className="flex min-h-[50vh] flex-1 items-center justify-center bg-[#f5f5ef] py-16">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
        </div>
      </MainLayout>
    );
  }

  if (!isAuthenticated) return null;

  if (loadError) {
    return (
      <MainLayout hideFooter>
        <div className="flex flex-1 items-center justify-center bg-[#f5f5ef] px-4 py-16">
          <div className="w-full max-w-md rounded-2xl border-2 border-red-300 bg-red-50 px-6 py-5 text-center">
            <p className="font-semibold text-red-800">{loadError}</p>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 text-sm font-bold text-indigo-700 underline-offset-2 hover:underline"
            >
              Đăng nhập lại
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideFooter>
      <div className="flex min-h-[calc(100dvh-72px)] flex-col bg-[#f5f5ef] px-3 py-2 sm:px-4 md:px-5 lg:px-6">
        <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col">
          <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-2">
            <Outlet context={{ profile, refetch: fetchProfile }} />
          </main>
        </div>
      </div>
    </MainLayout>
  );
};

export default AccountLayout;
